import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import defaultTo from 'lodash/defaultTo';
import Badge from '@atlaskit/badge';
import { CustomDynamicResultTable, ExpandableRow, TableHead, TableBody, ColGroup } from '@css-xp/base';
import { errorStateProp, emptyStateProp } from '@css-xp/ppc-common';
import { SelectType } from '../../../billing-profile/src/types';
import { CartProductSchema } from '../../../../types';
import { State } from '../../../../store/types';
import type {
  PresentationLayerProducts,
  Product,
  ProductIdPlanOptionsMap,
  LinkedEntitlements,
  cartLinkedEntitlement,
  StandaloneProductWithApps,
} from '../types';
import { CartLineItemRow } from './cart-line-item-row';
import './product-rows-container.scss';

const COLUMNS = {
  PRODUCT: 'Product',
  PLAN: 'Plan',
  CUSTOMER_CONTACT: 'Customer contact',
  LIST_PRICE: 'List price',
  PARTNER_DISCOUNT: 'Partner discount',
  OTHER_DISCOUNT: 'Other discounts',
  NEXT_BILL_DATE: 'Next bill date',
  ACTIVE_USERS: 'Active users',
  BILLING_CYCLE: 'Billing cycle',
  STATUS: 'Status',
  TOTAL: 'Total',
  TAX_ESTIMATE: 'Tax estimate',
};

interface GetColumnProps {
  showTotalColumn?: boolean;
  showTaxEstimationColumn?: boolean;
  cloudsiteRemovalCheckbox?: React.ReactNode;
  selectiveProductRemovalEnable?: boolean;
  hideCustomerContactColumn?: boolean;
}

const getColumns = ({
  showTotalColumn,
  showTaxEstimationColumn,
  cloudsiteRemovalCheckbox,
  selectiveProductRemovalEnable,
  hideCustomerContactColumn,
}: GetColumnProps) => {
  let columns = [
    {
      name: COLUMNS.PRODUCT,
      renderComponent: (
        <th className="product-rows-container--cloudsite-checkbox">
          {selectiveProductRemovalEnable ? cloudsiteRemovalCheckbox : <></>}
          <div className="product-rows-container--product-heading">Product</div>
        </th>
      ),
      customClassNames: 'col-product',
    },
    {
      name: COLUMNS.PLAN,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.CUSTOMER_CONTACT,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.TOTAL,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.LIST_PRICE,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.PARTNER_DISCOUNT,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.OTHER_DISCOUNT,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.TAX_ESTIMATE,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.NEXT_BILL_DATE,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.ACTIVE_USERS,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.BILLING_CYCLE,
      customClassNames: 'col-common',
    },
    {
      name: COLUMNS.STATUS,
      customClassNames: 'col-common',
    },
  ];

  if (!showTotalColumn) {
    columns = columns.filter((column) => column.name !== COLUMNS.TOTAL);
  }
  if (!showTaxEstimationColumn) {
    columns = columns.filter((column) => column.name !== COLUMNS.TAX_ESTIMATE);
  }
  if (hideCustomerContactColumn) {
    columns = columns.filter((column) => column.name !== COLUMNS.CUSTOMER_CONTACT);
  }
  return columns;
};

type Props = {
  products: PresentationLayerProducts;
  productIdPlanOptionsMap: ProductIdPlanOptionsMap;
  selectedBillDate?: string;
  checkedBillDate?: Record<string, string>;
  setSelectedPricingPlanId: (
    updatedPricingPlanId: string,
    updatedOfferingId: string,
    productId: CartProductSchema['id']
  ) => void;
  setActiveUsers: (plan: string, productId: CartProductSchema['id']) => void;
  onRemoveProductFromCart: (productId: CartProductSchema['id']) => void;
  cloudsiteRemovalCheckbox: React.ReactNode;
  handleCheckedProductLineItems: (productId: string) => void;
  checkedItemMap: Record<string, SelectType>;
  selectiveProductRemovalEnable: boolean;
  matchBillDateEnabled?: boolean;
  isStickyColumnEnabled?: boolean;
  showProductsPromotionView?: boolean;
  showTotalColumn?: boolean;
  showTaxEstimationColumn?: boolean;
  shouldShowTrialStatus?: boolean;
  enrichmentStatus?: State['enrichmentStatus'];
  isAllowedToEditUsersCount?: boolean;
  showUnchangedLineItems?: boolean;
  selectedProductIdsWithoutChanges?: string[];
  hideCustomerContactColumn?: boolean;
  isOrgProduct?: boolean;
  showSandboxProducts?: boolean;
  isSandboxDecouplingEnabled?: boolean;
  isNonCommercialPricingEnabled?: boolean;
};

export const ProductRowsContainer = ({
  products,
  productIdPlanOptionsMap,
  selectedBillDate,
  setSelectedPricingPlanId,
  setActiveUsers,
  onRemoveProductFromCart,
  cloudsiteRemovalCheckbox,
  handleCheckedProductLineItems,
  selectiveProductRemovalEnable,
  checkedBillDate,
  matchBillDateEnabled = false,
  isStickyColumnEnabled = false,
  showProductsPromotionView = false,
  showTotalColumn = false,
  showTaxEstimationColumn = false,
  checkedItemMap,
  shouldShowTrialStatus = false,
  enrichmentStatus,
  isAllowedToEditUsersCount,
  showUnchangedLineItems,
  selectedProductIdsWithoutChanges,
  hideCustomerContactColumn = false,
  isOrgProduct = false,
  showSandboxProducts = false,
  isSandboxDecouplingEnabled = false,
  isNonCommercialPricingEnabled = false,
}: Props) => {
  const getProductRow = useCallback(
    (product: Product) => {
      const isRowHighlighedForReview =
        showUnchangedLineItems &&
        selectedProductIdsWithoutChanges?.length &&
        selectedProductIdsWithoutChanges.includes(product.id);

      return (
        <CartLineItemRow
          lineItem={product}
          productIdPlanOptionsMap={productIdPlanOptionsMap}
          enrichmentStatus={enrichmentStatus}
          selectedBillDate={selectedBillDate}
          setSelectedPricingPlanId={setSelectedPricingPlanId}
          setActiveUsers={setActiveUsers}
          onRemoveProductFromCart={onRemoveProductFromCart}
          handleCheckedProductLineItems={handleCheckedProductLineItems}
          isApp={false}
          showTotalColumn={showTotalColumn}
          showTaxEstimationColumn={showTaxEstimationColumn}
          showProductsPromotionView={showProductsPromotionView}
          isChecked={!!checkedItemMap[product.id]}
          selectiveProductRemovalEnable={selectiveProductRemovalEnable}
          matchBillDateEnabled={matchBillDateEnabled}
          checkedBillDate={checkedBillDate}
          shouldShowTrialStatus={shouldShowTrialStatus}
          isAllowedToEditUsersCount={isAllowedToEditUsersCount}
          isRowHighlighedForReview={!!isRowHighlighedForReview}
          hideCustomerContactColumn={hideCustomerContactColumn}
          isOrgProduct={isOrgProduct}
          showSandboxProducts={showSandboxProducts}
          isSandboxDecouplingEnabled={isSandboxDecouplingEnabled}
          isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
        />
      );
    },
    [onRemoveProductFromCart, productIdPlanOptionsMap, selectedBillDate, setActiveUsers, setSelectedPricingPlanId]
  );

  const columns = getColumns({
    showTotalColumn,
    showTaxEstimationColumn,
    cloudsiteRemovalCheckbox,
    selectiveProductRemovalEnable,
    hideCustomerContactColumn,
  });
  const getAppRows = useCallback(
    (linkedEntitlements: LinkedEntitlements) => {
      const [_, ...remainingColumns] = columns;
      const remainingEmptyCells = remainingColumns.map((_, index) => <td key={index} />);

      const subRows = linkedEntitlements.map((app, index) => (
        <CartLineItemRow
          key={`${app.id}_${index}`}
          lineItem={app}
          enrichmentStatus={enrichmentStatus}
          productIdPlanOptionsMap={productIdPlanOptionsMap}
          selectedBillDate={selectedBillDate}
          setSelectedPricingPlanId={setSelectedPricingPlanId}
          setActiveUsers={setActiveUsers}
          onRemoveProductFromCart={onRemoveProductFromCart}
          handleCheckedProductLineItems={handleCheckedProductLineItems}
          isChecked={!!checkedItemMap[app.id]}
          selectiveProductRemovalEnable={selectiveProductRemovalEnable}
          matchBillDateEnabled={matchBillDateEnabled}
          checkedBillDate={checkedBillDate}
          isApp={true}
          showTotalColumn={showTotalColumn}
          showTaxEstimationColumn={showTaxEstimationColumn}
          showProductsPromotionView={showProductsPromotionView}
          shouldShowTrialStatus={shouldShowTrialStatus}
          hideCustomerContactColumn={hideCustomerContactColumn}
          showSandboxProducts={showSandboxProducts}
          isSandboxDecouplingEnabled={isSandboxDecouplingEnabled}
          isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
        />
      ));

      const expandableCell = (
        <td key="expandableCell">
          Integrated apps <Badge appearance="primary">{linkedEntitlements.length}</Badge>
        </td>
      );

      const expandableRowHeading = [[expandableCell, ...remainingEmptyCells]];

      return <ExpandableRow rowsAsCells={expandableRowHeading} subRows={subRows} />;
    },
    [onRemoveProductFromCart, productIdPlanOptionsMap, selectedBillDate, setActiveUsers, setSelectedPricingPlanId]
  );

  const sortLineItems = (a: Product | cartLinkedEntitlement, b: Product | cartLinkedEntitlement) => {
    const firstName = defaultTo(a?.name, a?.name ?? '');
    const secondName = defaultTo(b?.name, b?.name ?? '');
    return firstName.localeCompare(secondName);
  };

  const rows = useMemo(() => {
    // `productRows` will contain list of products as a table row
    const productRows: React.ReactNode[] = [];
    const jiraproductRows: React.ReactNode[] = [];
    const sortedProducts = (products as StandaloneProductWithApps[])?.sort(sortLineItems);

    /**
     * Building table rows based on the list of products a user selected.
     * A product can be standalone or part of a container. Same is decided based on the value of `isContainer` property.
     * If product is standalone, we can directly render the row and then render the apps (apps render pending implementation).
     * If product is part of a container, we need to render it along with all of it's siblings together and then render the apps (apps render pending implementation).
     */
    (sortedProducts as PresentationLayerProducts).forEach((product) => {
      const isJiraContainer = product.type === 'container';

      if (!isJiraContainer) {
        const productRow = getProductRow(product);
        productRows.push(productRow);
      } else {
        const sortedProducts = product.products?.sort(sortLineItems);
        sortedProducts.forEach((jiraProduct) => {
          const productRow = getProductRow(jiraProduct);
          jiraproductRows.push(productRow);
        });
      }

      if (product.linkedEntitlements.length > 0) {
        const sortedApps = product.linkedEntitlements?.sort(sortLineItems);
        const appRows = getAppRows(sortedApps);
        if (!isJiraContainer) {
          productRows.push(appRows);
        } else {
          jiraproductRows.push(appRows);
        }
      }
    });

    return [...productRows, ...jiraproductRows];
  }, [getAppRows, getProductRow, products]);

  return (
    <div
      className={classNames('product-rows-container', {
        'product-rows-container-sticky-table': isStickyColumnEnabled,
        'product-rows-container-checkbox-selection': selectiveProductRemovalEnable,
      })}
      data-testId="product-rows-container"
    >
      <CustomDynamicResultTable
        isLoading={false}
        noResults={false}
        isPaginationEnabled={false}
        selectableResults={true}
        error={null}
        boxShadow={false}
        emptyStateProps={emptyStateProp}
        errorStateProps={errorStateProp}
        hasTableWrapper={isStickyColumnEnabled}
        shouldShowHorizontalScrollAnimation={isStickyColumnEnabled}
      >
        <ColGroup columns={columns} />
        <TableHead columns={columns} />
        <TableBody>{rows}</TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};
